import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Headphones, Mic, PenTool, LogOut, Menu, X, Settings, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DarkModeToggle } from './DarkModeToggle';
import { settingsAPI, usersAPI } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ siteName: 'TCF Canada', logo: '' });
  const [centerCode, setCenterCode] = useState(null);

  useEffect(() => {
    // Get settings based on user's center
    const loadSettings = async () => {
      try {
        if (user?.id) {
          const settings = await settingsAPI.getEffectiveSettings(user.id);
          setSiteSettings({
            siteName: settings?.site_name || settings?.siteName || 'TCF Canada',
            logo: settings?.logo || ''
          });
          
          // Store center code for logout redirect based on user role
          if (user.role === 'super_admin') {
            // SuperAdmin should not have a center code - clear it
            setCenterCode(null);
            localStorage.removeItem('tcf_center_code');
          } else if (user.role === 'admin' && user.invitation_code) {
            setCenterCode(user.invitation_code);
            localStorage.setItem('tcf_center_code', user.invitation_code);
          } else if (user.role === 'user' && user.center_id) {
            // Fetch admin's invitation code
            try {
              const adminData = await usersAPI.getById(user.center_id);
              if (adminData?.invitation_code) {
                setCenterCode(adminData.invitation_code);
                localStorage.setItem('tcf_center_code', adminData.invitation_code);
              }
            } catch (e) {
              console.error('Could not fetch admin code:', e);
            }
          } else {
            // User without center - clear the code
            setCenterCode(null);
            localStorage.removeItem('tcf_center_code');
          }
        } else {
          const settings = await settingsAPI.getSiteSettings();
          setSiteSettings({
            siteName: settings?.site_name || settings?.siteName || 'TCF Canada',
            logo: settings?.logo || ''
          });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, [user]);

  const handleLogout = () => {
    // Get the center code from state or localStorage
    const code = centerCode || localStorage.getItem('tcf_center_code');
    
    // Determine the redirect URL based on user type
    let redirectUrl = '/';
    
    if (user?.role === 'super_admin') {
      // SuperAdmin goes to default landing page
      redirectUrl = '/';
      localStorage.removeItem('tcf_center_code');
    } else if (code) {
      // Admin or user with center code goes to their branded landing page
      redirectUrl = `/?code=${code}`;
    }
    
    // Navigate first, then logout to avoid ProtectedRoute redirect
    navigate(redirectUrl, { replace: true });
    // Small delay to ensure navigation happens before logout clears the user
    setTimeout(() => {
      logout();
    }, 100);
  };

  const menuItems = user?.role === 'super_admin' ? [
    { icon: Settings, label: 'Super Admin', path: '/super-admin' },
  ] : user?.role === 'admin' ? [
    { icon: Home, label: 'Dashboard Admin', path: '/admin-control' },
  ] : [
    { icon: Home, label: 'Tableau de bord', path: '/dashboard' },
    { icon: BookOpen, label: 'Compréhension Écrite', path: '/modules/comprehension-ecrite' },
    { icon: Headphones, label: 'Compréhension Orale', path: '/modules/comprehension-orale' },
    { icon: PenTool, label: 'Expression Écrite', path: '/modules/expression-ecrite' },
    { icon: Mic, label: 'Expression Orale', path: '/modules/expression-orale' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border shadow-sm"
      >
        {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r transition-transform duration-300 transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo and Site Name */}
          <div className="p-4 border-b">
            <div className="flex items-center space-x-3">
              {siteSettings.logo ? (
                <img 
                  src={siteSettings.logo.startsWith('http') ? siteSettings.logo : `${API_URL}${siteSettings.logo.startsWith('/') ? '' : '/'}${siteSettings.logo}`} 
                  alt={siteSettings.siteName || 'Logo'} 
                  className="h-14 w-14 object-contain rounded-xl shadow-md ring-2 ring-primary/20"
                />
              ) : (
                <div className="h-14 w-14 bg-gradient-to-br from-primary to-primary/70 rounded-xl flex items-center justify-center shadow-md">
                  <span className="text-2xl font-bold text-primary-foreground">
                    {(siteSettings.siteName || 'TCF').charAt(0)}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-primary truncate leading-tight">
                  {siteSettings.siteName || 'TCF Canada'}
                </h1>
                <p className="text-xs text-muted-foreground">Préparation TCF</p>
              </div>
            </div>
          </div>

          {/* User info */}
          <div className="p-4 border-b">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-semibold">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" data-testid="user-name">{user?.username}</p>
                <p className="text-xs text-muted-foreground">
                  {user?.role === 'admin' ? 'Administrateur' : `${user?.credits_ia || 0} essais IA`}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  data-testid={`nav-${item.path.split('/').pop()}`}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary text-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t space-y-2">
            <DarkModeToggle />
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-destructive hover:text-destructive-foreground transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span className="text-sm font-medium">Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};